import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  DefaultShapeStyle,
  HoveredShapeStyle,
  SelectedShapeStyle,
  DefaultLineStyle,
  HoveredLineStyle,
  SelectedLineStyle,
} from '../SheetsViewerV2.config';
import { LineStyle } from '../shapes/abstract/AbstractSheetLineShape';
import WedgeShape from '../shapes/WedgeShape';
import { SheetViewerV2Calibration } from './useCalibration';
import { useLegendEntriesContext } from './LegendEntriesContext';
import { DbIdsPerModel } from 'src/components/common/ForgeViewer';
import { getColorForShape, isShapeHidden } from 'src/components/SheetShapes';
import { SelectedDbIds } from 'src/components/viewers/common/hooks/useSelectedDbIds';
import { useSheetShapesManager } from 'src/components/viewers/common/hooks/useSheetShapesManager';
import { useSheetsVisibility } from 'src/components/viewers/common/hooks/useSheetsVisibilityManager';
import { useSheetsColorization } from 'src/components/viewers/common/hooks/useSheetsColorizationManager';
import { ViewerExtension } from 'src/components/viewers/common/utils/autodesk';
import { SelectTool } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/SelectTool';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import {
  ActiveSheet,
  ActiveSheetData,
} from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { ISheetShape } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import { PointShape } from 'src/components/viewers/SheetsViewerV2/shapes/PointShape';
import { PolygonShape } from 'src/components/viewers/SheetsViewerV2/shapes/PolygonShape';
import { PolylineShape } from 'src/components/viewers/SheetsViewerV2/shapes/PolylineShape';
import { SplitLineShape } from 'src/components/viewers/SheetsViewerV2/shapes/SplitLineShape';
import { LegendShape } from 'src/components/viewers/SheetsViewerV2/shapes/LegendShape';
import {
  clearTextLabels,
  upsertTextLabel,
} from 'src/components/viewers/SheetsViewerV2/utils/labels';
import Edit2D = Autodesk.Extensions.Edit2D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;
import Viewer3D = Autodesk.Viewing.Viewer3D;
import { useUserTenant } from 'src/services/auth-info';
import { ChakraColor } from 'src/utils/color-util';
import { decodeColorWithPattern } from 'src/utils/color-pattern-encoding';
import { getUnitScaleFromCalibration } from 'src/domain/sheet-calibration';
import {
  GetSparkelPropertiesForProjectQuery,
  SheetScaleType,
} from 'src/gql/graphql';
import { TaperedInsulationShapeClass } from 'src/components/viewers/SheetsViewerV2/shapes/TaperedInsulationShape';
import {
  getCrossSectionFromProperties,
  getProductAreasFromProperties,
  getRoofUValueFromProperties,
  getRoofShapeFromProperties,
} from 'src/components/orderContractor/pdf-shapes/SheetDrainLineDrawing';
import {
  computeTaperedInsulationPolygons,
  computePlanSlices,
} from 'src/components/orderContractor/pdf-shapes/protan/useTaperedInsulation';
import { usePropertyContext } from 'src/hooks/property-resolving';
import { getShapeUrn } from 'src/services/viewer/ShapesManager';
import { Substrate } from 'src/components/orderContractor/ProductChoices';
import {
  HISTORY_COLORS,
  getHistoricalShapes,
} from 'src/components/project/project-history/hooks/useDiffing';
import { useProjectHistory } from 'src/components/project/project-history/hooks/useProjectHistory';
import { EventType } from 'src/gql/graphql';

export function useShapes(
  viewer: Viewer3D | GuiViewer3D | null | undefined,
  activeTool: ActiveTool,
  activeSheet: ActiveSheet,
  selectedDbIds: SelectedDbIds,
  visibilityManager: ReturnType<typeof useSheetsVisibility>,
  showSheetShapeLabels: boolean,
  colorizationManager: ReturnType<typeof useSheetsColorization>,
  calibration: SheetViewerV2Calibration,
  shapesManager: ReturnType<typeof useSheetShapesManager>,
  propertyData?: GetSparkelPropertiesForProjectQuery,
  calibrationData?: SheetScaleType
) {
  const { isProtan } = useUserTenant();
  const labelsDivId = 'shape-labels';

  // Get current order ID from URL
  const { orderId: activeOrderId } = useParams<{ orderId?: string }>();

  // Get legend entries and selection from context (provided by order panels)
  const { getEntriesForOrder, getSelectedEntryIds } = useLegendEntriesContext();

  // Get shape folders from property context
  const { propertyContext } = usePropertyContext(
    activeSheet?.data?.projectId || ''
  );

  const shapeUrn = getShapeUrn(activeSheet?.data?.projectId || '');
  const shapeFolders = useMemo(
    () => propertyContext?.loadedShapeFolders?.[shapeUrn] || [],
    [propertyContext, shapeUrn]
  );

  // Helper function to create fallback split line shape
  const createFallbackSplitLineShape = (shape: any, defaultColor: string) => {
    return new SplitLineShape(
      viewer as Viewer3D | GuiViewer3D,
      edit2D as Edit2D,
      activeSheetData as ActiveSheetData,
      shape,
      undefined,
      {
        default: {
          ...DefaultLineStyle,
          lineColor: defaultColor,
          lineStyle: LineStyle.DashedLarge,
        },
        hovered: {
          ...HoveredLineStyle,
          lineColor: defaultColor,
          lineStyle: LineStyle.DashedLarge,
        },
        selected: SelectedLineStyle,
      }
    );
  };

  // Helper function to extract tapered insulation data
  const extractTaperedInsulationData = (shape: any) => {
    if (!propertyData) return null;

    const crossSection = getCrossSectionFromProperties(
      propertyData,
      shape.dbId,
      shape.urn
    );

    if (!crossSection) return null;

    // Resolve the underlying roof shape dbId (source for Substrate property)
    const roofShapeDbId = getRoofShapeFromProperties(
      propertyData,
      shape.dbId,
      shape.urn
    );

    // Resolve substrate from sparkel properties for the roof shape
    const substrateFromProps = (() => {
      const propertyValue = propertyData?.project?.sparkelProperties.find(
        (prop) =>
          prop.propertySet === 'Substrate' &&
          prop.dbId === roofShapeDbId &&
          prop.modelUrn === shape.urn
      )?.propertyValue;
      const validValues = Object.values(Substrate) as string[];
      return validValues.includes(propertyValue || '')
        ? (propertyValue as Substrate)
        : Substrate.Steel;
    })();

    const currentProductAreas = getProductAreasFromProperties(
      propertyData,
      shape.dbId,
      shape.urn,
      substrateFromProps
    );

    const currentRoofUValue = getRoofUValueFromProperties(
      propertyData,
      shape.dbId,
      shape.urn
    );

    return {
      crossSection,
      currentProductAreas,
      currentRoofUValue,
      roofShapeDbId,
      substrate: substrateFromProps,
    };
  };

  // Helper function to create tapered insulation shape
  const createTaperedInsulationShape = (shape: any, defaultColor: string) => {
    if (!viewer || !edit2D || !activeSheetData) {
      return createFallbackSplitLineShape(shape, defaultColor);
    }

    const insulationData = extractTaperedInsulationData(shape);
    if (!insulationData) {
      return createFallbackSplitLineShape(shape, defaultColor);
    }

    const {
      crossSection,
      currentProductAreas,
      currentRoofUValue,
      roofShapeDbId,
      substrate: substrateFromProps,
    } = insulationData;

    // Use the utility function to compute the tapered insulation data
    const { crossSectionPolygons } = computeTaperedInsulationPolygons({
      dbId: shape.dbId,
      crossSection,
      currentProductAreas,
      currentRoofUValue,
      roofShapeDbId: roofShapeDbId || undefined,
      substrate: (substrateFromProps as Substrate) || Substrate.Steel,
      unitScale,
      renderedSheetShapes: shapesManager.renderedSheetShapes,
    });

    // Compute plan slices separately
    const planSlices = computePlanSlices({
      crossSection,
      substrate: (substrateFromProps as Substrate) || Substrate.Steel,
      unitScale,
      renderedSheetShapes: shapesManager.renderedSheetShapes,
      roofShapeDbId: roofShapeDbId || undefined,
    });

    return new TaperedInsulationShapeClass(
      viewer,
      edit2D,
      activeSheetData,
      shape,
      undefined,
      {
        default: {
          ...DefaultLineStyle,
          lineColor: defaultColor,
          fillColor: defaultColor,
          lineStyle: LineStyle.DashedLarge,
          fillAlpha: 0.3,
        },
        hovered: {
          ...HoveredLineStyle,
          lineColor: defaultColor,
          fillColor: defaultColor,
          lineStyle: LineStyle.DashedLarge,
          fillAlpha: 0.3,
        },
        selected: {
          ...SelectedLineStyle,
          fillColor: defaultColor,
          lineStyle: LineStyle.DashedLarge,
          fillAlpha: 0.3,
        },
      },
      shapesManager.renderedSheetShapes,
      unitScale,
      propertyData,
      calibrationData,
      crossSectionPolygons,
      planSlices
    );
  };
  const [edit2D, setEdit2D] = useState<Autodesk.Extensions.Edit2D | null>(null);
  const activeSheetData = activeSheet.data;
  const { colorization, pattern } = colorizationManager;

  const unitScale = useMemo(() => {
    if (!calibration.existing) return 1;
    return getUnitScaleFromCalibration(calibration.existing);
  }, [calibration.existing]);

  // Build history map for sheet shapes (urn-id -> EventType) when in history mode
  const { isHistoryDrawerOpen, referenceState, diff } = useProjectHistory();
  const historyEventTypeByKey = useMemo(() => {
    if (!isHistoryDrawerOpen || !referenceState || !diff) return null;
    const current = Object.values(shapesManager.renderedSheetShapes).flat();
    const hist = getHistoricalShapes(referenceState, diff, current)?.filter(
      (s) => (s as any).__typename === 'SheetShape'
    );
    const map = new Map<string, EventType | undefined>();
    hist.forEach((s: any) => {
      map.set(`${s.urn}-${s.id}`, s.eventType as EventType | undefined);
    });
    return map;
  }, [
    isHistoryDrawerOpen,
    referenceState,
    diff,
    shapesManager.renderedSheetShapes,
  ]);

  // Compute which legends are auto-hidden because they don't match the active order
  const autoHiddenLegends = useMemo((): DbIdsPerModel => {
    const hidden: DbIdsPerModel = {};

    Object.values(shapesManager.renderedSheetShapes)
      .flatMap((shapes) => shapes)
      .forEach((shape) => {
        if (shape.sheetShapeLegend) {
          const legendOrderId = shape.sheetShapeLegend.legendOrderId;
          if (legendOrderId !== activeOrderId) {
            if (!hidden[shape.urn]) {
              hidden[shape.urn] = [];
            }
            hidden[shape.urn].push(shape.dbId);
          }
        }
      });

    return hidden;
  }, [shapesManager.renderedSheetShapes, activeOrderId]);

  const shapesToRender = useMemo(() => {
    if (!viewer || !edit2D || !activeSheetData) return null;

    const instances = Object.values(shapesManager.renderedSheetShapes)
      .flatMap((shapes) => shapes)
      .filter((shape) => {
        // Check if legend is auto-hidden due to not matching active order
        if (autoHiddenLegends[shape.urn]?.includes(shape.dbId)) {
          return false;
        }

        return !isShapeHidden(
          visibilityManager.hiddenElements,
          shape.urn,
          shape,
          visibilityManager.hiddenSheetShapes,
          visibilityManager.isolatedElements,
          false
        );
      })
      // eslint-disable-next-line complexity
      .map((shape) => {
        const historyColor = (() => {
          if (!historyEventTypeByKey) return undefined;
          const evt = historyEventTypeByKey.get(`${shape.urn}-${shape.id}`);
          return evt !== undefined
            ? HISTORY_COLORS[evt as keyof typeof HISTORY_COLORS]
            : undefined;
        })();
        if (shape.sheetShapePolygon) {
          // Get the encoded color (may contain pattern info)
          const encodedColor = getColorForShape(
            shape.urn,
            shape.dbId,
            colorization,
            false,
            historyColor
          );

          // Decode to get the base color and per-shape pattern
          const { color: defaultColor, pattern: shapePattern } =
            decodeColorWithPattern(encodedColor);

          // Use per-shape pattern if available, otherwise fall back to global pattern
          const effectivePattern = shapePattern ?? pattern;

          return new PolygonShape(
            viewer,
            edit2D,
            activeSheetData,
            shape,
            undefined,
            {
              default: {
                ...DefaultShapeStyle,
                fillColor: defaultColor,
                lineColor: defaultColor,
              },
              hovered: {
                ...HoveredShapeStyle,
                fillColor: defaultColor,
                lineColor: defaultColor,
              },
              selected: SelectedShapeStyle,
            },
            isProtan,
            Object.values(shapesManager.renderedSheetShapes).flat(),
            calibrationData,
            propertyData,
            effectivePattern
          );
        } else if (shape.sheetShapeLine) {
          const defaultColor = getColorForShape(
            shape.urn,
            shape.dbId,
            colorization,
            false,
            historyColor
          );

          const isWedge = isProtan && isWedgeShape(shape.name);

          return isWedge
            ? // Protan only supports wedge shapes
              new WedgeShape(
                viewer,
                edit2D,
                activeSheetData,
                shape,
                undefined,
                {
                  default: {
                    ...DefaultShapeStyle,
                    lineColor: ChakraColor.green300,
                    fillColor: ChakraColor.green300,
                    fillAlpha: 0.2,
                    lineWidth: 2,
                  },
                  hovered: {
                    ...HoveredShapeStyle,
                    lineColor: ChakraColor.green300,
                    fillColor: ChakraColor.green300,
                    fillAlpha: 0.2,
                    lineWidth: 2,
                  },
                  selected: SelectedShapeStyle,
                },
                shapesManager.renderedSheetShapes,
                unitScale,
                propertyData,
                calibrationData
              )
            : new PolylineShape(
                viewer,
                edit2D,
                activeSheetData,
                shape,
                undefined,
                {
                  default: {
                    ...DefaultLineStyle,
                    lineColor: defaultColor,
                  },
                  hovered: {
                    ...HoveredLineStyle,
                    lineColor: defaultColor,
                  },
                  selected: SelectedLineStyle,
                }
              );
        } else if (shape.sheetShapePoint) {
          const defaultColor = getColorForShape(
            shape.urn,
            shape.dbId,
            colorization,
            false,
            historyColor
          );

          return new PointShape(
            viewer,
            edit2D,
            activeSheetData,
            calibrationData,
            shape,
            undefined,
            {
              default: {
                ...DefaultShapeStyle,
                fillColor: defaultColor,
                lineColor: defaultColor,
                lineWidth: 0.5,
              },
              hovered: {
                ...HoveredShapeStyle,
                fillColor: defaultColor,
                lineColor: defaultColor,
                lineWidth: 0.5,
              },
              selected: SelectedShapeStyle,
            },
            isProtan
          );
        } else if (shape.sheetShapeLegend) {
          // Legend shape - renders a table of order entries
          // Get entries from context (provided by order panels when order is active)
          const legendOrderId = shape.sheetShapeLegend.legendOrderId;
          const entriesFromContext = legendOrderId
            ? getEntriesForOrder(legendOrderId)
            : undefined;
          const selectedEntryIds = legendOrderId
            ? getSelectedEntryIds(legendOrderId)
            : new Set<string>();

          // Use entries from context if available (order is active and resolved)
          // Otherwise fall back to orderEntries from the fragment (for regular orders)
          return new LegendShape(
            viewer,
            edit2D,
            activeSheetData,
            shape,
            undefined,
            undefined,
            entriesFromContext,
            selectedEntryIds
          );
        } else {
          // Split line
          const defaultColor = getColorForShape(
            shape.urn,
            shape.dbId,
            colorization,
            false,
            historyColor
          );

          // Check if this is a tapered insulation shape
          if (isTaperedInsulationShape(shape, propertyData, isProtan)) {
            return createTaperedInsulationShape(shape, defaultColor);
          }

          return new SplitLineShape(
            viewer,
            edit2D,
            activeSheetData,
            shape,
            undefined,
            {
              default: {
                ...DefaultLineStyle,
                lineColor: defaultColor,
                lineStyle: LineStyle.DashedLarge,
              },
              hovered: {
                ...HoveredLineStyle,
                lineColor: defaultColor,
                lineStyle: LineStyle.DashedLarge,
              },
              selected: SelectedLineStyle,
            }
          );
        }
      })
      .filter(Boolean);

    // Attach wind load zones listener for all polygon shapes to react to runtime toggle
    instances.forEach((instance) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (typeof instance.attachWindLoadZonesListener === 'function') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        instance.attachWindLoadZonesListener();
      }
    });

    return instances;
  }, [
    viewer,
    edit2D,
    activeSheetData,
    shapesManager.renderedSheetShapes,
    visibilityManager.hiddenElements,
    visibilityManager.hiddenSheetShapes,
    visibilityManager.isolatedElements,
    colorization,
    pattern,
    propertyData,
    calibrationData,
    getEntriesForOrder,
    getSelectedEntryIds,
    activeOrderId,
    autoHiddenLegends,
  ]);

  useEffect(() => {
    if (!viewer) return;

    const edit2D = viewer.getExtension(ViewerExtension.Edit2D) as Edit2D;

    if (!edit2D.defaultContext) {
      edit2D.registerDefaultTools();
    }

    setEdit2D(edit2D);
  }, [viewer]);

  // Effect for shape initialization and cleanup (runs when shapes change)
  useEffect(() => {
    if (!viewer || !edit2D?.defaultContext || !shapesToRender) return;

    const clearShapes = () => {
      clearTextLabels(viewer, labelsDivId);
      shapesToRender.map((instance) => instance.remove());
    };
    const updateTextLabelsCallback = () => {
      if (!showSheetShapeLabels) return;

      shapesToRender.forEach((model) => {
        model.getLabels({ shapeFolders, isProtan }).forEach(
          (
            label: {
              point: THREE.Vector3;
              label: string;
              rotation?: number;
              fontSize?: number;
            },
            index: number
          ) => {
            upsertTextLabel(viewer, labelsDivId, {
              ...label,
              urn: `${model.urn}-${model?.dbId}-${index}`,
            });
          }
        );
      });
    };

    // Draw all shapes to add them to the layer
    shapesToRender.forEach((model) => {
      model.draw();
    });

    updateTextLabelsCallback();

    viewer.addEventListener(
      Autodesk.Viewing.CAMERA_CHANGE_EVENT,
      updateTextLabelsCallback
    );
    viewer.addEventListener(Autodesk.Viewing.MODEL_REMOVED_EVENT, clearShapes);

    return () => {
      clearShapes();
      viewer.removeEventListener(
        Autodesk.Viewing.MODEL_REMOVED_EVENT,
        clearShapes
      );
      viewer.removeEventListener(
        Autodesk.Viewing.CAMERA_CHANGE_EVENT,
        updateTextLabelsCallback
      );
    };
  }, [
    activeSheet.data,
    edit2D?.defaultContext,
    shapesToRender,
    viewer,
    showSheetShapeLabels,
  ]);

  // Separate effect for selection updates (runs only when selection changes)
  useEffect(() => {
    if (!shapesToRender) return;

    // Set selection state for shapes (optimized to only update changed shapes)
    shapesToRender.forEach((model) => {
      const selectedDbIdsArray = model
        ? selectedDbIds.selectedDbIds[model.urn]
        : undefined;
      const shouldBeSelected =
        model &&
        Array.isArray(selectedDbIdsArray) &&
        selectedDbIdsArray.includes(model.dbId);

      // Only update if the selection state has actually changed
      if (model && shouldBeSelected !== model.selected) {
        if (shouldBeSelected) {
          model.setSelected(true);
        } else {
          model.setSelected(false);
        }
      }
    });
  }, [selectedDbIds.selectedDbIds, shapesToRender]);

  // Separate effect to handle label visibility toggle
  useEffect(() => {
    if (!viewer || !shapesToRender) return;

    if (!showSheetShapeLabels) {
      clearTextLabels(viewer, labelsDivId);
    } else {
      // Redraw labels when toggle is turned on
      shapesToRender.forEach((model) => {
        model.getLabels({ shapeFolders, isProtan }).forEach(
          (
            label: {
              point: THREE.Vector3;
              label: string;
              rotation?: number;
              fontSize?: number;
            },
            index: number
          ) => {
            upsertTextLabel(viewer, labelsDivId, {
              ...label,
              urn: `${model.urn}-${model?.dbId}-${index}`,
            });
          }
        );
      });
    }
  }, [
    showSheetShapeLabels,
    viewer,
    shapesToRender,
    labelsDivId,
    shapeFolders,
    isProtan,
  ]);

  useEffect(() => {
    if (
      !viewer ||
      !edit2D?.defaultContext ||
      !shapesToRender ||
      !activeSheetData
    )
      return;

    if (activeTool !== ActiveTool.Select) return; // omit registering and activating selecting tool

    const changeSelectedShapes = (selectedShapes: ISheetShape[]) => {
      // Build the selection state from the shapes provided by SelectTool
      // The SelectTool maintains its own selection state and passes all currently selected shapes
      const newSelectedDbIds = selectedShapes.reduce((acc, shape) => {
        if (!acc[shape.urn]) {
          acc[shape.urn] = [];
        }
        acc[shape.urn].push(shape.dbId);
        return acc;
      }, {} as DbIdsPerModel);

      selectedDbIds.setSelectedDbIds(newSelectedDbIds);
    };

    const selectTool = new SelectTool(
      viewer,
      shapesToRender,
      activeSheetData,
      edit2D,
      changeSelectedShapes
    );

    // Synchronize the tool's internal selection state with current selections
    selectTool.syncWithCurrentSelection();

    viewer.toolController.registerTool(selectTool);
    viewer.toolController.activateTool(selectTool.getName());

    return () => {
      // Before deactivating the tool, preserve the current selection state
      const currentSelectedShapes = Array.from(
        selectTool['_selected'].values()
      );

      const preservedSelection = currentSelectedShapes.reduce((acc, shape) => {
        if (!acc[shape.urn]) {
          acc[shape.urn] = [];
        }
        acc[shape.urn].push(shape.dbId);
        return acc;
      }, {} as DbIdsPerModel);

      // Update the global selection state to preserve the selection
      selectedDbIds.setSelectedDbIds(preservedSelection);

      viewer.toolController.deactivateTool(selectTool.getName());
      viewer.toolController.deregisterTool(selectTool);
    };
  }, [
    activeTool,
    activeSheetData,
    selectedDbIds.setSelectedDbIds,
    edit2D,
    shapesToRender,
    viewer,
  ]);

  return { shapesManager, autoHiddenLegends };
}

const isWedgeShape = (shapeName: string): boolean => {
  return shapeName.toLowerCase().includes('wedge');
};

// Helper function to identify if a split line shape is a tapered insulation shape
const isTaperedInsulationShape = (
  shape: any,
  propertyData?: GetSparkelPropertiesForProjectQuery,
  isProtan?: boolean
): boolean => {
  if (!isProtan || !propertyData || !shape.sheetShapeSplitLine) {
    return false;
  }

  const crossSection = getCrossSectionFromProperties(
    propertyData,
    shape.dbId,
    shape.urn
  );

  return crossSection !== null;
};
/*
const TaperedInsulationShapeComponent = (
  viewer: Viewer3D | GuiViewer3D,
  edit2d: Edit2D,
  activeSheetData: ActiveSheetData,
  shape: any,
  propertyData?: GetSparkelPropertiesForProjectQuery,
  calibrationData?: SheetScaleType
) => {
  const crossSection = getCrossSectionFromProperties(
    propertyData,
    shape.dbId,
    shape.urn
  );

  const { planSlices, crossSectionPolygons } = useTaperedInsulation({
    dbId: shape.dbId,
    crossSection: crossSection!,
    currentProductAreas: propertyData?.currentProductAreas || {},
    currentRoofUValue: propertyData?.currentRoofUValue || null,
    roofShapeDbId: propertyData?.roofShapeDbId || null,
    substrate: propertyData?.substrate || null,
  });

  return new TaperedInsulationShapeClass(
    viewer,
    edit2D,
    activeSheetData,
    shape,
    undefined,
    {
      default: {
        ...DefaultShapeStyle,
        fillColor: defaultColor,
        lineColor: defaultColor,
        fillAlpha: 0.3,
      },
      hovered: {
        ...HoveredShapeStyle,
        fillColor: defaultColor,
        lineColor: defaultColor,
        fillAlpha: 0.3,
      },
      selected: SelectedShapeStyle,
    },
    shapesManager.renderedSheetShapes,
    unitScale,
    propertyData,
    calibrationData
  );
};
*/
